import { useState } from "react";
import { Brain, ChevronDown, ChevronUp, Info, Lightbulb, MessageSquare, TrendingUp, TrendingDown, Minus } from "lucide-react";

// ─── Etiquetas legibles ───────────────────────────────────────────────────────
const CONDITION_LABELS = {
  depression:      { es: "Depresión",            icon: "😔", color: "#6366f1" },
  anxiety:         { es: "Ansiedad",              icon: "😰", color: "#f59e0b" },
  burnout:         { es: "Agotamiento / Burnout", icon: "🔥", color: "#ef4444" },
  loneliness:      { es: "Soledad / Aislamiento", icon: "🌫️", color: "#8b5cf6" },
  trauma:          { es: "Trauma / TEPT",         icon: "💔", color: "#ec4899" },
  eating_disorder: { es: "T. Alimentario",        icon: "⚖️", color: "#14b8a6" },
};

const SEVERITY_CONFIG = {
  low:      { label: "Leve",     dot: "#22c55e", bg: "rgba(34,197,94,0.08)",   border: "rgba(34,197,94,0.25)"  },
  moderate: { label: "Moderado", dot: "#f59e0b", bg: "rgba(245,158,11,0.08)", border: "rgba(245,158,11,0.30)" },
  high:     { label: "Alto",     dot: "#ef4444", bg: "rgba(239,68,68,0.08)",  border: "rgba(239,68,68,0.30)"  },
  critical: { label: "Crítico",  dot: "#7c3aed", bg: "rgba(124,58,237,0.10)", border: "rgba(124,58,237,0.40)" },
};

const PATTERN_LABELS = {
  absolutism:              "Pensamiento absolutista",
  catastrophizing:         "Catastrofismo",
  self_blame:              "Autocrítica / Culpa",
  helplessness:            "Indefensión",
  emotional_dysregulation: "Desregulación emocional",
};

// ─── Barra de severidad ───────────────────────────────────────────────────────
function SeverityBar({ severity, score }) {
  const pct = Math.round((score || 0) * 100);
  const colors = { low: "#22c55e", moderate: "#f59e0b", high: "#ef4444", critical: "#7c3aed" };
  const color = colors[severity] || "#6b7280";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ flex: 1, height: 4, borderRadius: 99, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%", background: color, borderRadius: 99,
          transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)"
        }} />
      </div>
      <span style={{ fontSize: 10, color: "#9ca3af", minWidth: 28 }}>{pct}%</span>
    </div>
  );
}

// ─── Sección clínica ─────────────────────────────────────────────────────────
function ClinicalSection({ title, icon: Icon, children, accentColor = "#0d9488" }) {
  return (
    <div style={{
      marginBottom: 10,
      borderRadius: 8,
      border: `1px solid ${accentColor}22`,
      background: `${accentColor}08`,
      overflow: "hidden",
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "6px 10px",
        borderBottom: `1px solid ${accentColor}18`,
        background: `${accentColor}10`,
      }}>
        <Icon style={{ width: 11, height: 11, color: accentColor }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: accentColor, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {title}
        </span>
      </div>
      <div style={{ padding: "8px 10px" }}>
        {children}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
/**
 * Props:
 *   message   — mensaje del usuario (is_user: true)
 *   aiMessage — siguiente mensaje de la IA (is_user: false) con observacion_clinica, etc.
 */
export default function MentalHealthIndicator({ message, aiMessage }) {
  const [expanded, setExpanded] = useState(false);

  // ── Datos del mensaje usuario ──
  const isAlert             = message?.is_alert;
  const mhDetected          = message?.mental_health_detected;
  const severity            = message?.mental_health_severity;
  const primary             = message?.mental_health_primary;
  const conditions          = message?.mental_health_conditions || [];
  const patterns            = message?.mental_health_linguistic_patterns || [];
  const score               = message?.mental_health_severity_score;
  const trend               = message?.mental_health_conversation_trend;
  const msgsFlags           = message?.mental_health_messages_with_flags;
  const totalMsgs           = message?.mental_health_total_messages_analyzed;

  // ── Datos del mensaje IA (orientación clínica) ──
  const observacion         = aiMessage?.observacion_clinica || null;
  const preguntas           = aiMessage?.preguntas_sugeridas || [];
  const consejo             = aiMessage?.consejo_avance || null;

  // No mostrar nada si no hay nada relevante
  const hasAnything = mhDetected || isAlert || observacion || preguntas.length > 0 || consejo;
  if (!hasAnything) return null;

  // Si hay alerta pero severity es none/null, usar "high" para que se muestre bien
  const effectiveSeverity = severity && severity !== "none" ? severity : (isAlert ? "high" : "low");
  const sev     = SEVERITY_CONFIG[effectiveSeverity] || SEVERITY_CONFIG.low;
  const primCf  = primary ? CONDITION_LABELS[primary] : null;

  const hasClinicalData = observacion || preguntas.length > 0 || consejo;

  // Icono de tendencia
  const TrendIcon = trend === "worsening" ? TrendingUp : trend === "improving" ? TrendingDown : Minus;
  const trendColor = trend === "worsening" ? "#ef4444" : trend === "improving" ? "#22c55e" : "#6b7280";
  const trendLabel = trend === "worsening" ? "Empeorando" : trend === "improving" ? "Mejorando" : "Estable";

  return (
    <div style={{ marginTop: 4, marginBottom: 2, maxWidth: 460, alignSelf: "flex-end" }}>

      {/* ── Pastilla compacta ── */}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6,
          padding: "3px 10px 3px 7px", borderRadius: 99,
          border: `1px solid ${sev.border}`, background: sev.bg,
          cursor: "pointer", fontSize: 11, color: "#d1d5db",
          lineHeight: 1.4, backdropFilter: "blur(4px)",
          transition: "all 0.2s", outline: "none", userSelect: "none",
        }}
      >
        {/* Dot */}
        <span style={{
          width: 7, height: 7, borderRadius: "50%", background: sev.dot,
          flexShrink: 0, boxShadow: `0 0 6px ${sev.dot}`,
          animation: effectiveSeverity === "critical" ? "pulse-dot 1.5s ease-in-out infinite" : "none"
        }} />

        <Brain style={{ width: 11, height: 11, color: sev.dot, flexShrink: 0 }} />

        {/* Label condición */}
        {primCf ? (
          <span>
            <span style={{ marginRight: 3 }}>{primCf.icon}</span>
            <span style={{ color: "#f3f4f6", fontWeight: 600 }}>{primCf.es}</span>
          </span>
        ) : isAlert ? (
          <span style={{ color: "#f3f4f6", fontWeight: 600 }}>Riesgo detectado</span>
        ) : (
          <span style={{ color: "#f3f4f6", fontWeight: 600 }}>Indicador emocional</span>
        )}

        {/* Badge severidad */}
        <span style={{
          padding: "1px 6px", borderRadius: 99, background: sev.dot + "22",
          color: sev.dot, fontWeight: 700, fontSize: 10,
          letterSpacing: "0.03em", textTransform: "uppercase",
        }}>
          {sev.label}
        </span>

        {/* Badge clínico si hay orientación */}
        {hasClinicalData && (
          <span style={{
            padding: "1px 6px", borderRadius: 99,
            background: "rgba(13,148,136,0.15)", color: "#2dd4bf",
            fontWeight: 600, fontSize: 10,
          }}>
            + Orientación
          </span>
        )}

        {expanded
          ? <ChevronUp  style={{ width: 12, height: 12, color: "#9ca3af" }} />
          : <ChevronDown style={{ width: 12, height: 12, color: "#9ca3af" }} />
        }
      </button>

      {/* ── Panel expandido ── */}
      {expanded && (
        <div style={{
          marginTop: 6, padding: "12px 14px", borderRadius: 12,
          border: `1px solid ${sev.border}`,
          background: "rgba(10,18,35,0.92)",
          backdropFilter: "blur(16px)",
          fontSize: 12, color: "#d1d5db",
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          animation: "fadeSlideIn 0.2s ease",
        }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
            <Brain style={{ width: 14, height: 14, color: sev.dot }} />
            <span style={{ fontWeight: 700, color: "#f9fafb", fontSize: 12 }}>
              Análisis clínico
            </span>
            <span style={{
              marginLeft: "auto", padding: "2px 7px", borderRadius: 99,
              background: sev.dot + "20", color: sev.dot,
              fontWeight: 700, fontSize: 10, textTransform: "uppercase",
            }}>
              {sev.label}
            </span>
          </div>

          {/* Barra de señal */}
          {score !== undefined && score !== null && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 10, color: "#9ca3af" }}>
                <span>Nivel de señal detectada</span>
              </div>
              <SeverityBar severity={effectiveSeverity} score={score} />
            </div>
          )}

          {/* ── SECCIÓN: Observación clínica ── */}
          {observacion && (
            <ClinicalSection title="Observación clínica" icon={Brain} accentColor="#6366f1">
              <p style={{ fontSize: 11.5, color: "#e2e8f0", lineHeight: 1.6, margin: 0 }}>
                {observacion}
              </p>
            </ClinicalSection>
          )}

          {/* ── SECCIÓN: Preguntas sugeridas ── */}
          {preguntas.length > 0 && (
            <ClinicalSection title="Preguntas sugeridas" icon={MessageSquare} accentColor="#0d9488">
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {preguntas.map((q, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span style={{
                      flexShrink: 0, width: 18, height: 18, borderRadius: "50%",
                      background: "rgba(13,148,136,0.2)", border: "1px solid rgba(13,148,136,0.4)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, fontWeight: 700, color: "#2dd4bf",
                    }}>
                      {i + 1}
                    </span>
                    <p style={{ fontSize: 11.5, color: "#cbd5e1", lineHeight: 1.55, margin: 0 }}>
                      {q}
                    </p>
                  </div>
                ))}
              </div>
            </ClinicalSection>
          )}

          {/* ── SECCIÓN: Consejo de avance ── */}
          {consejo && (
            <ClinicalSection title="Cómo avanzar" icon={Lightbulb} accentColor="#f59e0b">
              <p style={{ fontSize: 11.5, color: "#e2e8f0", lineHeight: 1.6, margin: 0 }}>
                {consejo}
              </p>
            </ClinicalSection>
          )}

          {/* ── Condiciones detectadas ── */}
          {conditions.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 10, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                Indicadores detectados
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {conditions.map(cond => {
                  const cf = CONDITION_LABELS[cond];
                  if (!cf) return null;
                  const isPrimary = cond === primary;
                  return (
                    <span key={cond} style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      padding: "3px 9px", borderRadius: 99,
                      border: isPrimary ? `1.5px solid ${cf.color}` : "1px solid rgba(255,255,255,0.1)",
                      background: isPrimary ? cf.color + "22" : "rgba(255,255,255,0.04)",
                      color: isPrimary ? "#f9fafb" : "#9ca3af",
                      fontWeight: isPrimary ? 700 : 400, fontSize: 11,
                    }}>
                      <span>{cf.icon}</span>
                      <span>{cf.es}</span>
                      {isPrimary && (
                        <span style={{ fontSize: 9, color: cf.color, fontWeight: 700, marginLeft: 2 }}>
                          PRINCIPAL
                        </span>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Patrones lingüísticos ── */}
          {patterns.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <p style={{ fontSize: 10, color: "#6b7280", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                Patrones del lenguaje
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {patterns.map(pat => (
                  <span key={pat} style={{
                    padding: "2px 7px", borderRadius: 6,
                    background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)",
                    color: "#fbbf24", fontSize: 10,
                  }}>
                    {PATTERN_LABELS[pat] || pat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Stats conversacionales ── */}
          {(totalMsgs > 0 || trend) && (
            <div style={{
              padding: "7px 10px", borderRadius: 8, marginBottom: 10,
              background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
              display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center",
            }}>
              {totalMsgs > 0 && <StatPill label="Mensajes analizados" value={totalMsgs} />}
              {msgsFlags !== undefined && msgsFlags !== null && (
                <StatPill label="Con señales" value={msgsFlags} highlight={msgsFlags > 0} />
              )}
              {trend && trend !== "stable" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <span style={{ fontSize: 9, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Tendencia
                  </span>
                  <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 600, color: trendColor }}>
                    <TrendIcon style={{ width: 11, height: 11 }} />
                    {trendLabel}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Aviso */}
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 6,
            padding: "6px 8px", borderRadius: 8,
            background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)",
          }}>
            <Info style={{ width: 12, height: 12, color: "#818cf8", flexShrink: 0, marginTop: 1 }} />
            <p style={{ fontSize: 10, color: "#94a3b8", margin: 0, lineHeight: 1.5 }}>
              Análisis automático orientativo para uso clínico. No sustituye diagnóstico profesional.
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-dot {
          0%, 100% { box-shadow: 0 0 4px #7c3aed; }
          50%       { box-shadow: 0 0 10px #7c3aed, 0 0 20px #7c3aed44; }
        }
      `}</style>
    </div>
  );
}

function StatPill({ label, value, highlight }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <span style={{ fontSize: 9, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </span>
      <span style={{ fontSize: 11, fontWeight: 600, color: highlight ? "#f59e0b" : "#d1d5db" }}>
        {value}
      </span>
    </div>
  );
}